import { createSelector } from '@reduxjs/toolkit';
import {
  IBuilder,
  IMeasureFilterValue,
  IRowMeta,
  RowType,
} from '../slices/oldBuilder';
import {
  IEventConditionFieldsFragment,
  QueryType,
  SimpleCohortTypeKey,
} from '../../utils/apollo/graphqlSchema';
import {
  cohortRowMetaTypes,
  funnelAvailableOrders,
  isMeasureCohortFilter,
  selectErrorRow,
  selectEventCondition,
  selectEventConditionsCollection,
  selectEventFilterCondition,
  selectRow,
} from '../slices/oldBuilder/utils';
import { assertCondition, assertNotNil, byTypeName } from '../../utils/types';
import { findByKey } from '../../utils/redux/util';
import { getCharacterByIncrementedIndex } from '../../utils/character';
import { selectorArg1 } from '../utils/selectors';
import { RootState } from '../store';
import { ISignificanceErrors } from '../slices/oldBuilder/significance/types';
import { UnreachableCaseError } from 'ts-essentials';

export const builderSelector = ({ builder }: RootState) => builder;

export const builderPropertySelector = <T extends keyof IBuilder>() =>
  createSelector(
    builderSelector,
    selectorArg1<T>(),
    (builder, propKey) => builder[propKey],
  );

export const activeSchemaIdSelector = createSelector(
  builderSelector,
  (builder) => builder.activeSchemaId,
);

export const defaultLogOpsSelector = createSelector(
  builderSelector,
  (builder) => builder.defaultLogOps,
);

export const builderSampledCohortSelector = createSelector(
  builderSelector,
  (builder) => builder.builderSampledCohort,
);

export const builderDateConfigSelector = createSelector(
  builderSelector,
  (builder) => builder.dateConfig,
);
export const measuresSelector = createSelector(
  builderSelector,
  (builder) => builder.measures,
);

export const measureFormulaSelector = createSelector(
  builderSelector,
  (builder) => builder.measureFormula,
);

export const dataTableMeasuresSelector = createSelector(
  builderSelector,
  (builder) => builder.dataTableMeasures,
);

export const measureRowsSelector = createSelector(
  builderSelector,
  (_: RootState, rowType: RowType) => rowType,
  (builder, rowType) => {
    if (rowType === RowType.dataTableMeasures) {
      return builder.dataTableMeasures;
    }

    if (rowType === RowType.measures) {
      return builder.measures;
    }

    return [];
  },
);

export const cohortConfigTypeSelector = createSelector(
  builderSelector,
  (_: RootState, simpleCohortTypeKey: SimpleCohortTypeKey) =>
    simpleCohortTypeKey,
  (builder, simpleCohortTypeKey) => {
    switch (simpleCohortTypeKey) {
      case SimpleCohortTypeKey.Expression:
        return builder.cohortConfig.expressionConfig;
      case SimpleCohortTypeKey.Funnel:
        return builder.cohortConfig.funnelConfig;
      case SimpleCohortTypeKey.Measure:
        return builder.cohortConfig.measureConfig;
      case SimpleCohortTypeKey.Performed:
        return builder.cohortConfig.performedConfig;
      case SimpleCohortTypeKey.Retention:
        return builder.cohortConfig.retentionConfig;
      default:
        throw new UnreachableCaseError(simpleCohortTypeKey);
    }
  },
);

export const compoundMeasureUserAliasIdSelector = createSelector(
  builderSelector,
  (builder) => builder.compoundMeasureUserAliasId,
);

export const rowSelector = createSelector(
  builderSelector,
  (_: RootState, rowMeta: IRowMeta) => rowMeta,
  (builder, rowMeta) => {
    return selectRow(builder, rowMeta);
  },
);

export const rowsSelector = createSelector(
  builderSelector,
  (_: RootState, rowMeta: IRowMeta) => rowMeta,
  (builder, rowMeta) => {
    const { rowType, rowIndex } = rowMeta;
    if (isMeasureCohortFilter(rowMeta)) {
      const cohortFilter = builder[rowType][rowIndex][rowMeta.cohortRowType];
      return cohortFilter ? [cohortFilter] : [];
    }

    return builder[rowType];
  },
);

export const stepsSelector = createSelector(
  builderSelector,
  (builder) => builder.steps,
);

export const currentStepSelector = createSelector(
  builderSelector,
  selectorArg1<number>(),
  (builder, rowIndex) => builder.steps[rowIndex],
);

export const stepOrdersSelector = (rowMeta: IRowMeta) =>
  createSelector(builderSelector, (builder) => {
    // step order at rowIndex = N is at index N - 1
    return rowMeta.rowIndex === 0
      ? null
      : builder.stepOrders[rowMeta.rowIndex - 1];
  });

export const functionPrimitiveSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, rowMeta) => {
      const { rowType } = rowMeta;
      if (
        rowType === RowType.global ||
        rowType === RowType.prediction ||
        rowType === RowType.trigger ||
        rowType === RowType.paths ||
        rowType === RowType.expandEvents ||
        rowType === RowType.mergeEvents ||
        rowType === RowType.foldEvents ||
        rowType === RowType.retention
      ) {
        return null;
      }

      return selectRow(builder, rowMeta).fn;
    },
  );
};

export const eventPropertyPrimitiveSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, rowMeta) => {
      const eventCondition = selectEventCondition(builder, rowMeta);
      assertNotNil(eventCondition);
      return eventCondition.field;
    },
  );
};

export const expandEventFieldPrimitiveSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, { rowType, rowIndex }) => {
      if (rowType === RowType.expandEvents) {
        return builder[rowType][rowIndex].field;
      } else {
        return null;
      }
    },
  );
};

export const eventTimeWindowSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, rowMeta) => {
      const { rowType, numberIndex } = rowMeta;

      if (
        cohortRowMetaTypes.includes(rowType) ||
        isMeasureCohortFilter(rowMeta)
      ) {
        return selectRow(builder, rowMeta).timeWindowValues[numberIndex!];
      }
      if (rowType !== RowType.global) {
        if (rowType === RowType.prediction) {
          return builder.prediction.timeWindow;
        }
      } else {
        return builder.globalFrequency && builder.globalFrequency.timeWindow;
      }
    },
  );
};

export const filterPrimitiveSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, rowMeta) => {
      const { filter } = selectEventFilterCondition(builder, rowMeta);
      return filter;
    },
  );
};

export const eventConditionsPrimitiveSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, rowMeta) => {
      const { rowIndex } = rowMeta;

      // base case for rowIndex = -1
      if (rowIndex < 0) {
        return null;
      }
      return selectEventConditionsCollection(builder, rowMeta);
    },
  );
};

export const eventsPrimitiveSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, rowMeta) => {
      if (rowMeta.rowType === RowType.global) {
        return [];
      } else {
        const eventCondition: IEventConditionFieldsFragment =
          selectEventCondition(builder, rowMeta);

        if (!eventCondition) {
          return [];
        }
        const events = eventCondition.events || [];
        const anyEvents = events.filter(byTypeName('AnyEvent'));
        const basicEvents = events.filter(byTypeName('BasicEventValue'));
        const virtualEvents = events.filter(byTypeName('VirtualEvent'));
        const anyTableEvents = events.filter(byTypeName('AnyTableEvent'));

        return [basicEvents, virtualEvents, anyEvents, anyTableEvents];
      }
    },
  );
};

export const errorPrimitiveSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (_: RootState, __: IRowMeta, errorKey: string) => errorKey,
    (builder, rowMeta, errorKey) => {
      const {
        rowType,
        rowIndex,
        eventIndex,
        numberIndex,
        negativeStepRowIndex,
      } = rowMeta;

      if (rowType === RowType.footer) {
        return builder?.errors?.[rowType];
      }

      if (
        cohortRowMetaTypes.includes(rowType) ||
        isMeasureCohortFilter(rowMeta)
      ) {
        const errorRow = selectErrorRow(builder, rowMeta);

        if (typeof eventIndex === 'number' && eventIndex >= 0) {
          return errorRow?.[errorKey]?.[eventIndex];
        }

        if (typeof numberIndex === 'number' && numberIndex >= 0) {
          return errorRow?.[errorKey]?.[numberIndex];
        }

        return errorRow?.[errorKey];
      }

      if (rowType === RowType.expandEvents || rowType === RowType.foldEvents) {
        return builder?.errors?.[rowType]?.[rowIndex];
      }

      if (
        typeof eventIndex === 'number' &&
        eventIndex >= 0 &&
        (rowType === RowType.mergeEvents ||
          rowType === RowType.paths ||
          rowType === RowType.retention)
      ) {
        return builder?.errors?.[rowType]?.[rowIndex]?.[eventIndex];
      }

      if (rowType === RowType.global && errorKey === 'timeWindow') {
        return builder?.errors?.global?.[errorKey];
      }

      if (rowType === RowType.prediction) {
        return builder?.errors?.[rowType]?.[errorKey];
      }

      if (rowType === RowType.trigger) {
        return builder?.errors?.[rowType];
      }

      if (typeof negativeStepRowIndex === 'number') {
        return builder?.errors?.[rowType]?.[rowIndex]?.[errorKey]?.[
          negativeStepRowIndex
        ];
      }

      if (typeof eventIndex === 'number' && eventIndex >= 0 && errorKey) {
        return builder?.errors?.[rowType]?.[rowIndex]?.[errorKey]?.[eventIndex];
      }

      return builder?.errors?.[rowType]?.[rowIndex]?.[errorKey];
    },
  );
};

export const cachedCohortSelectorById = createSelector(
  builderSelector,
  (_: RootState, cohortId: string) => cohortId,
  (builder, cohortId) => {
    return builder.cachedCohorts?.[cohortId] ?? null;
  },
);

export const negativeStepRowSelector = createSelector(
  builderSelector,
  (_: RootState, rowMeta: IRowMeta) => rowMeta,
  (builder, rowMeta) => {
    const { rowIndex, rowType } = rowMeta;

    assertCondition(rowType === RowType.steps);

    return builder.steps[rowIndex].negativeSteps;
  },
);

export const conditionTypeOptionsSelector = createSelector(
  builderSelector,
  (builder) => builder.cohortConfig.simpleCohortTypes,
);

export const funnelOrdersSelector = createSelector(builderSelector, (builder) =>
  // TODO fix cast
  funnelAvailableOrders(builder as any),
);

export const functionPercentileSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, { rowType, rowIndex }) => {
      return builder[rowType][rowIndex].percentile;
    },
  );
};

export const builderSamplingRateSelector = createSelector(
  builderSelector,
  (builder) => builder.formulaSamplingRate,
);

export const builderCohortSamplePercentageSelector = () => {
  return createSelector(
    builderSelector,
    (_: RootState, rowMeta: IRowMeta) => rowMeta,
    (builder, rowMeta) => {
      const draftRow = selectRow(builder, rowMeta);
      return draftRow.contextInput.samplePercentage;
    },
  );
};

export const measureFiltersSelector = createSelector(
  builderSelector,
  (builder) => {
    const { queryType } = builder;
    if (queryType === QueryType.Expression) {
      const { measures, measureFormula, resultFilter } = builder;

      const result: IMeasureFilterValue[] = [];

      measures.forEach(({ measureFilter }, index) => {
        result.push({
          measureKey: getCharacterByIncrementedIndex('A', index),
          rowMeta: {
            rowType: RowType.measures,
            rowIndex: index,
          },
          filter: measureFilter,
        });
      });

      // We will add a result filter option for Compound measure in case
      // when formula has been set
      if (measures.length > 1 && measureFormula?.expression) {
        result.push({
          measureKey: measureFormula.expression,
          rowMeta: {
            rowType: RowType.global,
            rowIndex: -1,
          },
          filter: resultFilter,
        });
      }

      return result;
    }

    if (queryType === QueryType.DataTable) {
      const dataTableMeasures = builder.dataTableMeasures;

      return dataTableMeasures.map((measure, index) => ({
        measureKey: getCharacterByIncrementedIndex('A', index),
        rowMeta: {
          rowType: RowType.dataTableMeasures,
          rowIndex: index,
        },
        filter: measure.measureFilter,
      }));
    }

    return [];
  },
);

export const builderDefaultOptionsSelector = createSelector(
  builderSelector,
  (builder) => builder.defaultOptions,
);

export const builderSelectedOptionsSelector = createSelector(
  builderSelector,
  (builder) => builder.selectedOptions,
);

export const selectTimeUnits = createSelector(builderSelector, (builder) => {
  const { queryType, defaultOptions, selectedOptions } = builder;

  if (
    queryType === QueryType.Funnel &&
    defaultOptions?.type === queryType &&
    selectedOptions?.type === queryType
  ) {
    const { dateKinds, timeUnits } = defaultOptions;
    const { dateKind } = selectedOptions;

    // intersect the time units from dateKind (trigger, install, user) and from funnel time units from mode
    return findByKey(dateKinds, dateKind).supportedTimeUnits.filter((tu) =>
      timeUnits.includes(tu),
    );
  }

  if (queryType === QueryType.Retention && defaultOptions?.type === queryType) {
    const { defaultCohortUnits } = defaultOptions;

    return defaultCohortUnits;
  }

  if (
    (queryType === QueryType.Expression || queryType === QueryType.DataTable) &&
    defaultOptions?.type === queryType &&
    selectedOptions?.type === queryType
  ) {
    const { dateKinds } = defaultOptions;
    const { dateKind } = selectedOptions;

    return (
      dateKinds.find((dk) => dk.key === dateKind)?.supportedTimeUnits ?? []
    );
  }

  return [];
});

export const comparisonConfigSelector = createSelector(
  builderSelector,
  (builder) => builder.comparisonConfig,
);
export const defaultAdditionalSettingsSelector = createSelector(
  builderSelector,
  (builder) => builder.defaultAdditionalSettings,
);
export const additionalSettingsSelector = createSelector(
  builderSelector,
  (builder) => builder.additionalSettings,
);

export const significanceLevelSelector = createSelector(
  builderSelector,
  (builder) => builder.significance.significanceLevel,
);
export const significanceConfidenceLevelSelector = createSelector(
  builderSelector,
  (builder) => builder.significance.confidenceLevel,
);
export const significanceCriteriaSelector = createSelector(
  builderSelector,
  (builder) => builder.significance.criteria,
);
export const significanceMetricSelector = createSelector(
  builderSelector,
  (builder) => builder.significance.metric,
);
export const significanceErrorSelector = createSelector(
  builderSelector,
  selectorArg1<keyof ISignificanceErrors>(),
  (builder, errorKey) => builder.significance.errors[errorKey],
);
